import React from 'react';
import get from 'get-value';
import classNames from 'classnames';
import { FieldProps } from 'formik';
import Input, { IMaskedFieldProps } from 'shared/Inputs/Input';
import { removeArrayBrackets } from 'utils';
import FormControl from 'shared/FormControl/FormControl';
import './_QuoteField.scss';
import { HelperContent } from 'shared/HelperText/HelperTextInterface';

export interface QuoteFields extends FieldProps {
  labelText?: string;
  paragraphText?: string;
  tooltip?: string;
  isValidNested?: boolean;
  isCorrectNested?: boolean;
  inputSymbol?: string;
  inputSymbolPosition?: 'left' | 'right';
  type?: string;
  helperText?: string;
  helperContent?: HelperContent;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  handleBlur?: () => void;
  bottomHelperText?: string;
  transformValue?: (value: string) => string;
  bottomTextVariant?: ColorVariants;
  errorMessage?: string;
}

enum ColorVariants {
  RED = 'red',
}

const displaySymbol: React.FC<string> = (inputSymbol: string, inputSymbolPosition: 'left' | 'right') => {
  return (
    <div className={`form-control__currency form-control__currency-${inputSymbolPosition}`}>
      <div className="currency__symbol">{inputSymbol}</div>
    </div>
  );
};

export const QuoteField: React.FC<QuoteFields & IMaskedFieldProps> = ({
  labelText,
  paragraphText,
  field,
  form,
  isValidNested,
  isCorrectNested,
  inputSymbol = '',
  inputSymbolPosition = 'left',
  type = 'text',
  placeholder = '',
  mask,
  showMask,
  required,
  helperText,
  helperContent,
  disabled,
  handleBlur,
  bottomHelperText,
  children,
  transformValue,
  bottomTextVariant,
  errorMessage = undefined,
}) => {
  const { errors, touched } = form;
  const fieldName = removeArrayBrackets(field.name);
  const isValid = get(errors, fieldName) && get(touched, fieldName);
  const isCorrect = !get(errors, fieldName) && get(touched, fieldName);

  const bottomHelperClass = classNames('bottom-helper-text', {
    [`bottom-helper-text--${bottomTextVariant}`]: bottomTextVariant,
  });

  return (
    <FormControl
      labelText={labelText}
      paragraphText={paragraphText}
      error={isValidNested || isValid}
      helperText={helperText}
      helperContent={helperContent}
      isCorrect={isCorrectNested || isCorrect}
      required={required}
      hasIcon={inputSymbol.length > 0}
      fieldName={fieldName}
      errorMessage={errorMessage}>
      {inputSymbol ? displaySymbol(inputSymbol, inputSymbolPosition) : null}
      <Input
        field={field}
        type={type}
        className={inputSymbol ? 'currency' : ''}
        placeholder={placeholder}
        mask={mask}
        form={form}
        showMask={showMask}
        disabled={disabled}
        handleBlur={handleBlur}
        transformValue={transformValue}
      />
      <>
        {bottomHelperText && <p className={bottomHelperClass}>{bottomHelperText}</p>}
        {children}
      </>
    </FormControl>
  );
};
